import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { SHARED_DOCUMENTS, getParticipant } from './communicationsData';

const ROLE_BADGE = {
  investor: { label: 'Investor', bg: 'bg-primary/8', text: 'text-primary' },
  sponsor: { label: 'Sponsor', bg: 'bg-emerald-50', text: 'text-emerald-700' },
  admin: { label: 'Admin', bg: 'bg-purple-50', text: 'text-purple-700' },
};

function FileIcon({ type }) {
  const config = {
    pdf: { icon: 'FileText', color: 'text-red-600', bg: 'bg-red-50' },
    xlsx: { icon: 'FileSpreadsheet', color: 'text-emerald-600', bg: 'bg-emerald-50' },
    docx: { icon: 'FileText', color: 'text-[#3b4c4c]', bg: 'bg-primary/8' },
  };
  const c = config?.[type] || config?.pdf;
  return (
    <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${c?.bg} flex-shrink-0`}>
      <Icon name={c?.icon} size={15} className={c?.color} />
    </div>
  );
}

function CollapsibleSection({ title, icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-border last:border-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon name={icon} size={14} color="var(--color-muted-foreground)" />
          <span className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide">{title}</span>
        </div>
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={14} color="var(--color-muted-foreground)" />
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
}

export default function RightPanel({ thread, onClose }) {
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [addToast, setAddToast] = useState(false);
  const [notifPref, setNotifPref] = useState('all');
  const [emailDigest, setEmailDigest] = useState(true);

  const threadParticipants = thread?.participants?.map(id => getParticipant(id)) || [];
  const createdDate = new Date('2025-12-10')?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  const handleAddParticipant = () => {
    setShowAddParticipant(false);
    setAddToast(true);
    setTimeout(() => setAddToast(false), 3000);
  };

  return (
    <div className="flex flex-col h-full bg-card border-l border-border relative">
      {/* Toast */}
      {addToast && (
        <div className="absolute top-4 left-4 right-4 z-50 flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-600 text-white text-xs font-medium shadow-lg">
          <Icon name="CheckCircle" size={14} />
          Participant added
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="font-heading text-sm font-semibold text-foreground">Thread Details</h3>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
          <Icon name="X" size={15} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {/* About */}
        <CollapsibleSection title="About this thread" icon="Info">
          <div className="space-y-2.5">
            <div>
              <p className="font-caption text-xs text-muted-foreground mb-0.5">Thread name</p>
              <p className="font-caption text-sm font-medium text-foreground">{thread?.name || '—'}</p>
            </div>
            {thread?.dealLinked && (
              <div>
                <p className="font-caption text-xs text-muted-foreground mb-0.5">Linked deal</p>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-primary/8 text-primary cursor-pointer hover:bg-primary/15 transition-colors">
                  <Icon name="Link2" size={10} />
                  {thread?.dealLinked}
                </span>
              </div>
            )}
            <div>
              <p className="font-caption text-xs text-muted-foreground mb-0.5">Created by</p>
              <p className="font-caption text-sm text-foreground">Sarah Chen (Sponsor)</p>
            </div>
            <div>
              <p className="font-caption text-xs text-muted-foreground mb-0.5">Created</p>
              <p className="font-caption text-sm text-foreground">{createdDate}</p>
            </div>
            <div>
              <p className="font-caption text-xs text-muted-foreground mb-0.5">Description</p>
              <p className="font-caption text-xs text-foreground leading-relaxed">Investment Committee discussion thread for the Mersey Tidal Array project. Covers technical due diligence, financial modelling, and IC preparation. Sample / demo data.</p>
            </div>
          </div>
        </CollapsibleSection>

        {/* Participants */}
        <CollapsibleSection title="Participants" icon="Users">
          <div className="space-y-2">
            {threadParticipants?.map(p => {
              const roleConfig = ROLE_BADGE?.[p?.role] || ROLE_BADGE?.investor;
              return (
                <div key={p?.id} className="flex items-center gap-2.5">
                  <div className="relative flex-shrink-0">
                    <div
                      className="flex items-center justify-center w-8 h-8 rounded-full text-white text-xs font-semibold"
                      style={{ background: p?.color }}
                    >
                      {p?.initials}
                    </div>
                    {p?.online && (
                      <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-card" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-caption text-xs font-medium text-foreground truncate">{p?.name}</p>
                    <span className={`font-caption text-xs px-1.5 py-0.5 rounded font-medium ${roleConfig?.bg} ${roleConfig?.text}`}>{roleConfig?.label}</span>
                  </div>
                  <button className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-muted text-muted-foreground transition-all" title="Remove">
                    <Icon name="X" size={11} />
                  </button>
                </div>
              );
            })}
          </div>
          {!showAddParticipant ? (
            <button
              onClick={() => setShowAddParticipant(true)}
              className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-border text-xs font-medium text-muted-foreground hover:border-primary/40 hover:text-primary hover:bg-primary/4 transition-all"
            >
              <Icon name="UserPlus" size={13} />
              Add Participant
            </button>
          ) : (
            <div className="mt-3 p-3 rounded-xl border border-border bg-muted space-y-2">
              <input
                type="text"
                placeholder="Search users…"
                className="w-full px-2.5 py-1.5 font-caption text-xs bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
              <select className="w-full px-2.5 py-1.5 font-caption text-xs bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20">
                <option>Select role…</option>
                <option>Investor</option>
                <option>Sponsor</option>
                <option>Admin</option>
              </select>
              <div className="flex gap-2">
                <button
                  onClick={handleAddParticipant}
                  className="flex-1 py-1.5 rounded-lg bg-primary text-white text-xs font-medium hover:bg-primary/90 transition-colors"
                >
                  Add
                </button>
                <button
                  onClick={() => setShowAddParticipant(false)}
                  className="flex-1 py-1.5 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </CollapsibleSection>

        {/* Shared Documents */}
        <CollapsibleSection title="Shared Documents" icon="Paperclip">
          <div className="space-y-2">
            {SHARED_DOCUMENTS?.map(doc => (
              <div key={doc?.id} className="flex items-center gap-2">
                <FileIcon type={doc?.type} />
                <div className="flex-1 min-w-0">
                  <p className="font-caption text-xs font-medium text-foreground truncate">{doc?.name}</p>
                  <p className="font-caption text-xs text-muted-foreground">{doc?.sharedBy} · {new Date(doc.sharedDate)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p>
                </div>
                <div className="flex items-center gap-0.5">
                  <button className="p-1 rounded hover:bg-muted text-muted-foreground transition-colors" title="Preview">
                    <Icon name="Eye" size={12} />
                  </button>
                  <button className="p-1 rounded hover:bg-muted text-muted-foreground transition-colors" title="Download">
                    <Icon name="Download" size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button className="mt-3 flex items-center gap-1.5 font-caption text-xs text-primary font-medium hover:underline">
            <Icon name="FolderOpen" size={12} />
            Open in Data Room
          </button>
        </CollapsibleSection>

        {/* Deal References */}
        <CollapsibleSection title="Deal References" icon="Briefcase">
          {thread?.dealLinked ? (
            <div className="flex items-center gap-2.5 p-2.5 rounded-xl border border-primary/15 bg-primary/4">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Briefcase" size={14} color="var(--color-primary)" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-caption text-xs font-semibold text-foreground">{thread?.dealLinked}</p>
                <span className="font-caption text-xs px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 font-medium">IC Review</span>
              </div>
              <Icon name="ExternalLink" size={12} color="var(--color-muted-foreground)" className="flex-shrink-0" />
            </div>
          ) : (
            <p className="font-caption text-xs text-muted-foreground">No deal references in this thread.</p>
          )}
        </CollapsibleSection>

        {/* Notifications */}
        <CollapsibleSection title="Notifications" icon="Bell">
          <div className="space-y-3">
            <div>
              <p className="font-caption text-xs font-medium text-muted-foreground mb-2">Notify me of</p>
              <div className="space-y-1.5">
                {[{ id: 'all', label: 'All messages' }, { id: 'mentions', label: 'Mentions only' }, { id: 'mute', label: 'Mute' }]?.map(opt => (
                  <label key={opt?.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="notif"
                      value={opt?.id}
                      checked={notifPref === opt?.id}
                      onChange={() => setNotifPref(opt?.id)}
                      className="accent-[#3b4c4c]"
                    />
                    <span className="font-caption text-xs text-foreground">{opt?.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-caption text-xs text-muted-foreground">Email digest</span>
              <button
                onClick={() => setEmailDigest(e => !e)}
                className={`relative w-9 h-5 rounded-full transition-colors ${
                  emailDigest ? 'bg-primary' : 'bg-muted'
                }`}
              >
                <span
                  className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                    emailDigest ? 'translate-x-4' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
          </div>
        </CollapsibleSection>
      </div>
    </div>
  );
}
